import { IconButton } from '@telegram-apps/telegram-ui'
import {
	Fragment,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router'
import {
	ArrowLeft,
	Copy,
	MoreHorizontal,
	Pencil,
	Plus,
	Scissors,
	Trash2,
} from 'lucide-react'
import { toast } from 'sonner'
import { useAdminTrpc } from 'shared/api'
import type { AdminOutputs } from 'shared/api'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { CopyChip } from 'shared/components/CopyChip'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'shared/components/ui/dialog'
import {
	Drawer,
	DrawerContent,
	DrawerHeader,
	DrawerTitle,
} from 'shared/components/ui/drawerv2'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { Separator } from 'shared/components/ui/separator'

import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import { hasAdminPermission } from 'shared/lib/permissions/permissions'
import { useAuthStore } from 'shared/store/auth'
import { TrackCodeCaptureModal } from './TrackCodeCaptureModal'
import { prepareScannerAudio } from './scanner-audio'
import { getChangedFlightName } from '../lib/flight-name'
import {
	drainCancellation,
	drainInitiatorNotification,
	shouldShowInitiatorBanner,
} from '../lib/banners'
import type { Bucket } from '../lib/state'

type LotDetail = AdminOutputs['lots']['GetById']
type Allocation = LotDetail['allocations'][number]

type TrackCode = { id: string; value: string }
type CaptureState = {
	buyerUserId: string
	mode: 'scanner' | 'manual'
	editing?: TrackCode
}

/**
 * Sellers are not given the buyer's Telegram handle or id, so the label falls
 * through to whatever identifier they do get.
 */
function buyerLabel(buyer: {
	buyerTezId?: string
	buyerName?: string
	buyerTelegramId?: number
}): string {
	return (
		buyer.buyerTezId ??
		buyer.buyerName ??
		(buyer.buyerTelegramId ? `id${buyer.buyerTelegramId}` : '—')
	)
}

function newTrackCodeId() {
	return (
		crypto.randomUUID?.() ??
		`${Date.now()}-${Math.random().toString(36).slice(2)}`
	)
}

function allocationOwedQuantity(allocation: Allocation): number {
	return allocation.lines.reduce((sum, line) => sum + line.owedQuantity, 0)
}

export function AdminLotDetail() {
	const { id } = useParams<{ id: string }>()
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { t, prefixT } = useTranslate('pages.adminLotDetail')
	const user = useAuthStore((state) => state.user)
	// Sellers only ever see their own lots; the creator line is for staff.
	const canSeeCreator = user ? hasAdminPermission(user, 'lots_admin') : false
	// The client page is finance-only (it is where refunds happen), so for
	// everyone else the buyer stays plain text.
	const canOpenClient = user ? hasAdminPermission(user, 'finance_admin') : false
	const [trackCodesOverride, setTrackCodesOverride] = useState<{
		lotId: string
		byBuyer: Record<string, TrackCode[]>
	} | null>(null)
	const [capture, setCapture] = useState<CaptureState | null>(null)
	const [openTrackCodeMenuId, setOpenTrackCodeMenuId] = useState<string | null>(
		null,
	)
	const [flightNameDialogOpen, setFlightNameDialogOpen] = useState(false)
	const [flightNameDraft, setFlightNameDraft] = useState('')
	const [cancelPending, setCancelPending] = useState<number | null>(null)
	const [actionsOpen, setActionsOpen] = useState(false)
	const longPressTimerRef = useRef<number | null>(null)
	const longPressHandledRef = useRef(false)
	const { data: lot, refetch } = useQuery(
		trpc.lots.GetById.queryOptions({ id: id! }),
	)
	const lotLabels: Record<Bucket, string> = {
		draft: t('status.lot.draft'),
		collecting: t('status.lot.collecting'),
		ready: t('status.lot.ready'),
		purchased: t('status.lot.purchased'),
		paused: t('status.lot.paused'),
		cancelled: t('status.lot.cancelled'),
	}
	const slotLabels = {
		free: t('status.slot.free'),
		paid: t('status.slot.paid'),
	}
	const hydratedTrackCodesByBuyer = useMemo<Record<string, TrackCode[]>>(
		() =>
			lot
				? Object.fromEntries(
						lot.allocations.map((allocation) => [
							allocation.buyerUserId,
							allocation.trackCodes.map((value) => ({
								id: newTrackCodeId(),
								value,
							})),
						]),
					)
				: {},
		[lot],
	)
	const activeTrackCodesOverride =
		lot && trackCodesOverride?.lotId === lot._id ? trackCodesOverride : null
	const trackCodesByBuyer =
		activeTrackCodesOverride?.byBuyer ?? hydratedTrackCodesByBuyer
	const invalidateAll = () => {
		refetch()
		queryClient.invalidateQueries(trpc.lots.List.pathFilter())
		queryClient.invalidateQueries(trpc.dashboard.Summary.pathFilter())
	}
	const toggle = useMutation(
		trpc.lots.Toggle.mutationOptions({
			onSuccess: (r) => {
				toast.success(
					r.state === 'paused' ? prefixT('paused') : prefixT('resumed'),
				)
				invalidateAll()
			},
		}),
	)
	const publish = useMutation(
		trpc.lots.Publish.mutationOptions({
			onSuccess: () => {
				toast.success(prefixT('published'))
				invalidateAll()
			},
			onError: () => toast.error(prefixT('publishFailed')),
		}),
	)
	const markPurchased = useMutation(
		trpc.lots.MarkPurchased.mutationOptions({
			onSuccess: (r) => {
				toast.success(prefixT('notified', { notified: String(r.notified), total: String(r.total) }))
				invalidateAll()
			},
			onError: (e) => toast.error(e.message),
		}),
	)
	const duplicate = useMutation(trpc.lots.Duplicate.mutationOptions())
	const closeUnfilled = useMutation(trpc.lots.CloseUnfilled.mutationOptions())
	const cancelOrResume = useMutation(trpc.lots.CancelOrResume.mutationOptions())
	const retryInitiator = useMutation(
		trpc.lots.RetryInitiatorNotification.mutationOptions(),
	)
	const setTrackCodes = useMutation(trpc.lots.SetTrackCodes.mutationOptions())
	const setFlightName = useMutation(trpc.lots.SetFlightName.mutationOptions())

	useEffect(
		() => () => {
			if (longPressTimerRef.current !== null)
				window.clearTimeout(longPressTimerRef.current)
		},
		[],
	)

	const openManualEntry = useCallback((buyerUserId: string, message?: string) => {
		if (message) toast.info(message)
		setCapture((current) =>
			current && current.buyerUserId === buyerUserId
				? { ...current, mode: 'manual' }
				: { buyerUserId, mode: 'manual' },
		)
	}, [])

	const openScanner = useCallback(
		(buyerUserId: string) => {
			if (!navigator.mediaDevices?.getUserMedia) {
				openManualEntry(buyerUserId, prefixT('cameraUnavailable'))
				return
			}
			// This is intentionally initiated by the button press so the success beep is allowed on mobile.
			void prepareScannerAudio().catch(() => undefined)
			setCapture({ buyerUserId, mode: 'scanner' })
		},
		[openManualEntry, prefixT],
	)

	const clearLongPress = () => {
		if (longPressTimerRef.current === null) return
		window.clearTimeout(longPressTimerRef.current)
		longPressTimerRef.current = null
	}

	const startLongPress = (buyerUserId: string) => {
		clearLongPress()
		longPressHandledRef.current = false
		longPressTimerRef.current = window.setTimeout(() => {
			longPressTimerRef.current = null
			longPressHandledRef.current = true
			openManualEntry(buyerUserId)
		}, 550)
	}

	const persistTrackCodes = async (
		buyerUserId: string,
		trackCodes: TrackCode[],
		successMessage: string,
	) => {
		try {
			const result = await setTrackCodes.mutateAsync({
				id: lot!._id,
				buyerUserId,
				trackCodes: trackCodes.map((trackCode) => trackCode.value),
			})
			setTrackCodesOverride({
				lotId: lot!._id,
				byBuyer: {
					...trackCodesByBuyer,
					[buyerUserId]: result.trackCodes.map((value, index) => ({
						id: trackCodes[index]?.id ?? newTrackCodeId(),
						value,
					})),
				},
			})
			toast.success(successMessage)
			return true
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: prefixT('trackCodesSaveFailed'),
			)
			return false
		}
	}

	const saveTrackCode = async (value: string) => {
		if (!capture) return
		const currentCapture = capture
		const trimmedValue = value.trim()
		if (!trimmedValue) return
		const buyerCodes = trackCodesByBuyer[currentCapture.buyerUserId] ?? []
		const nextCodes = currentCapture.editing
			? buyerCodes.map((code) =>
					code.id === currentCapture.editing?.id
						? { ...code, value: trimmedValue }
						: code,
				)
			: [...buyerCodes, { id: newTrackCodeId(), value: trimmedValue }]
		const saved = await persistTrackCodes(
			currentCapture.buyerUserId,
			nextCodes,
			currentCapture.editing ? prefixT('trackCodeUpdated') : prefixT('trackCodeAdded'),
		)
		if (saved) setCapture(null)
	}

	const deleteTrackCode = async (buyerUserId: string, trackCodeId: string) => {
		const nextCodes = (trackCodesByBuyer[buyerUserId] ?? []).filter(
			(code) => code.id !== trackCodeId,
		)
		await persistTrackCodes(buyerUserId, nextCodes, prefixT('trackCodeDeleted'))
	}

	const openFlightNameDialog = () => {
		setFlightNameDraft(lot?.flightName ?? '')
		setFlightNameDialogOpen(true)
	}

	const saveFlightName = async () => {
		if (!lot) return
		const flightName = getChangedFlightName(flightNameDraft, lot.flightName)
		if (!flightName) return
		try {
			const result = await setFlightName.mutateAsync({
				id: lot._id,
				flightName,
			})
			setFlightNameDialogOpen(false)
			toast.success(
				prefixT('notified', {
					notified: String(result.notified),
					total: String(result.total),
				}),
			)
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : prefixT('flightNameSaveFailed'),
			)
		}
	}

	const copyTrackCode = (value: string) => {
		navigator.clipboard?.writeText(value)
		toast.success(prefixT('trackCodeCopied'))
	}

	// Both the "Cancel lot" button and the stalled-cancellation banner drive the
	// same bounded procedure to completion: it flips the lot on the first call
	// and then refunds a batch per call until nothing is pending.
	const runCancellation = async (lotId: string, confirmFirst: boolean) => {
		if (confirmFirst && !window.confirm(prefixT('cancelConfirm'))) return
		try {
			const remaining = await drainCancellation(
				lotId,
				(target) => cancelOrResume.mutateAsync({ lotId: target }),
				{ onProgress: (pending) => setCancelPending(pending) },
			)
			if (remaining > 0) {
				toast.error(prefixT('cancelPartial', { count: String(remaining) }))
			} else {
				toast.success(prefixT('cancelDone'))
			}
		} catch (e) {
			toast.error(e instanceof Error ? e.message : prefixT('cancelFailed'))
		} finally {
			setCancelPending(null)
			invalidateAll()
		}
	}

	const retryInitiatorNotification = async (lotId: string) => {
		try {
			const notified = await drainInitiatorNotification(lotId, (target) =>
				retryInitiator.mutateAsync({ id: target }),
			)
			if (notified) toast.success(prefixT('initiatorNotified'))
			else toast.error(prefixT('initiatorNotifyFailed'))
		} catch (e) {
			toast.error(e instanceof Error ? e.message : prefixT('initiatorNotifyFailed'))
		} finally {
			invalidateAll()
		}
	}

	const duplicateLot = async () => {
		try {
			const duplicated = await duplicate.mutateAsync({ id: lot!._id })
			toast.success(prefixT('duplicated'))
			navigate(`/admin/lots/${duplicated._id}`)
		} catch (error) {
			toast.error(error instanceof Error ? error.message : prefixT('duplicateFailed'))
		}
	}

	// The lot never filled, so the buyers who did pay stop waiting on the ones who
	// never came: the unsold rows are dropped and the lot becomes 'ready', from
	// where "Mark purchased" ships them. With `carryOver` the unsold rows are
	// re-listed as a new collecting lot instead of being discarded.
	const closeUnfilledLot = async (carryOver: boolean) => {
		if (
			!window.confirm(
				carryOver
					? prefixT('closeUnfilledCarryConfirm')
					: prefixT('closeUnfilledConfirm'),
			)
		)
			return
		try {
			const { remainderLotId } = await closeUnfilled.mutateAsync({
				id: lot!._id,
				carryOver,
			})
			toast.success(
				remainderLotId
					? prefixT('closeUnfilledCarryDone')
					: prefixT('closeUnfilledDone'),
			)
			if (remainderLotId) navigate(`/admin/lots/${remainderLotId}`)
		} catch (error) {
			toast.error(
				error instanceof Error ? error.message : prefixT('closeUnfilledFailed'),
			)
		}
	}

	const paidProgress = useMemo(() => {
		if (!lot) return { paid: 0, total: 0 }
		if (lot.inventory.type === 'slots') {
			return {
				paid: lot.inventory.items.filter((item) => item.status === 'paid')
					.length,
				total: lot.inventory.items.length,
			}
		}
		return {
			paid: lot.inventory.items.reduce((sum, item) => sum + item.soldQuantity, 0),
			total: lot.inventory.items.reduce(
				(sum, item) => sum + item.maxQuantity,
				0,
			),
		}
	}, [lot])

	if (!lot) return <div className='p-4'>{t('common.loading')}</div>
	const changedFlightName = getChangedFlightName(flightNameDraft, lot.flightName)
	const canSaveFlightName =
		Boolean(changedFlightName) &&
		!setFlightName.isPending
	const showInitiatorBanner = shouldShowInitiatorBanner(lot)
	const isCancelling = cancelPending !== null || cancelOrResume.isPending
	// Closing short is only meaningful while the lot is still taking money and
	// somebody has already paid: with nothing sold there is no one to close it
	// for, and the backend refuses that outright.
	const canCloseUnfilled =
		(lot.state === 'collecting' || lot.state === 'paused') &&
		paidProgress.paid > 0
	// Every action closes the sheet: what happens next is a toast, a navigation,
	// or the refund banner underneath — none of which are visible behind it.
	const runAction = (action: () => void) => () => {
		setActionsOpen(false)
		action()
	}
	// Grouped by what the action does to the lot — move it forward, change it,
	// close it early, stop it — and separated only where a group actually has
	// something in it for this state.
	const actionGroups: ReactNode[][] = [
		[
			lot.state === 'draft' && (
				<Button
					key='publish'
					className='w-full'
					disabled={publish.isPending}
					onClick={runAction(() => publish.mutate({ id: lot._id }))}
				>
					{prefixT('publish')}
				</Button>
			),
			lot.state === 'ready' && (
				<Button
					key='mark-purchased'
					className='w-full'
					disabled={markPurchased.isPending}
					onClick={runAction(() => markPurchased.mutate({ id: lot._id }))}
				>
					{prefixT('markPurchased')}
				</Button>
			),
			lot.state === 'purchased' && (
				<Button
					key='flight-name'
					className='w-full'
					// The flight dialog is a page-level dialog, so the sheet has to go
					// first — one over the other would stack two overlays.
					onClick={runAction(openFlightNameDialog)}
				>
					{lot.flightName
						? prefixT('editFlightName', { flightName: lot.flightName })
						: prefixT('setFlightName')}
				</Button>
			),
		],
		[
			(lot.state === 'draft' ||
				lot.state === 'collecting' ||
				lot.state === 'paused') && (
				<Button
					key='edit'
					variant='outline'
					className='w-full'
					onClick={runAction(() => navigate(`/admin/lots/${lot._id}/edit`))}
				>
					<Pencil className='mr-2 size-4' />
					{prefixT('editLot')}
				</Button>
			),
			<Button
				key='duplicate'
				variant='outline'
				className='w-full'
				disabled={duplicate.isPending}
				onClick={runAction(duplicateLot)}
			>
				<Copy className='mr-2 size-4' />
				{prefixT('duplicate')}
			</Button>,
		],
		[
			canCloseUnfilled && (
				<Button
					key='close-unfilled-carry'
					variant='outline'
					className='w-full'
					disabled={closeUnfilled.isPending}
					onClick={runAction(() => closeUnfilledLot(true))}
				>
					<Scissors className='mr-2 size-4' />
					{prefixT('closeUnfilledCarry')}
				</Button>
			),
			canCloseUnfilled && (
				<Button
					key='close-unfilled'
					variant='outline'
					className='w-full'
					disabled={closeUnfilled.isPending}
					onClick={runAction(() => closeUnfilledLot(false))}
				>
					{prefixT('closeUnfilled')}
				</Button>
			),
		],
		[
			(lot.state === 'collecting' || lot.state === 'paused') && (
				<Button
					key='toggle'
					variant='outline'
					className='w-full'
					disabled={toggle.isPending}
					onClick={runAction(() => toggle.mutate({ id: lot._id }))}
				>
					{lot.state === 'collecting' ? prefixT('pause') : prefixT('resume')}
				</Button>
			),
			lot.state !== 'purchased' && lot.state !== 'cancelled' && (
				<Button
					key='cancel'
					variant='destructive'
					className='w-full'
					disabled={isCancelling}
					onClick={runAction(() => runCancellation(lot._id, true))}
				>
					{prefixT('cancelLot')}
				</Button>
			),
		],
	]
		.map((group) => group.filter(Boolean))
		.filter((group) => group.length > 0)
	return (
		<div className='flex flex-col gap-4 p-4'>
			<div className='flex items-center gap-2'>
				<Button variant='ghost' size='icon' onClick={() => navigate(-1)}>
					<ArrowLeft className='size-5' />
				</Button>
				<h1 className='flex-1 truncate text-lg font-semibold'>{lot.title}</h1>
				<Badge
					variant={
						lot.state === 'ready'
							? 'default'
							: lot.state === 'cancelled'
								? 'destructive'
								: lot.state === 'draft'
									? 'outline'
									: 'secondary'
					}
				>
					{lotLabels[lot.state as Bucket]}
				</Badge>
			</div>

			{showInitiatorBanner && (
				<div className='rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-sm'>
					<div className='font-medium'>{prefixT('initiatorNotNotified')}</div>
					<Button
						variant='outline'
						className='mt-2 w-full'
						disabled={retryInitiator.isPending}
						onClick={() => retryInitiatorNotification(lot._id)}
					>
						{prefixT('retryInitiator')}
					</Button>
				</div>
			)}

			{lot.pendingRefundCount > 0 && (
				<div className='rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm'>
					<div className='font-medium'>
						{prefixT('cancellationStalled', {
							count: String(lot.pendingRefundCount),
						})}
					</div>
					{cancelPending !== null && (
						<div className='mt-1 text-muted-foreground'>
							{prefixT('refunding', { count: String(cancelPending) })}
						</div>
					)}
					<Button
						variant='destructive'
						className='mt-2 w-full'
						disabled={isCancelling}
						onClick={() => runCancellation(lot._id, false)}
					>
						{prefixT('resumeRefunds')}
					</Button>
				</div>
			)}

			<div className='flex gap-2 overflow-x-auto'>
				{lot.photoUrls.map((url) => (
					<img
						key={url}
						src={url}
						alt={t('common.photoAlt', { title: lot.title, index: String(lot.photoUrls.indexOf(url) + 1) })}
						className='h-24 w-24 rounded-lg object-cover'
					/>
				))}
			</div>
			<CopyChip
				value={String(lot.orderNumber)}
				label={t('common.orderNumber', { number: String(lot.orderNumber) })}
				className='w-fit'
			/>
			<div className='flex items-center justify-between text-sm'>
				<span className='font-medium'>{t('common.amountUzs', { amount: formatUzsAmount(lot.priceUzs) })}</span>
				<span>
					{prefixT('paidProgress', { paid: String(paidProgress.paid), total: String(paidProgress.total) })}
				</span>
			</div>
			{canSeeCreator && lot.createdByName && (
				<div className='text-sm text-muted-foreground'>
					{prefixT('createdBy', { name: lot.createdByName })}
				</div>
			)}
			{lot.notes && (
				<div className='text-sm whitespace-pre-wrap rounded-md bg-muted p-3'>
					{lot.notes}
				</div>
			)}
			{lot.storeUrl && (
				<a
					href={lot.storeUrl}
					target='_blank'
					rel='noreferrer'
					className='text-sm text-primary underline underline-offset-2'
				>
					{prefixT('openStoreLink')}
				</a>
			)}

			{lot.inventory.type === 'slots' ? (
				<div className='flex flex-col divide-y rounded-lg border'>
					{lot.inventory.items.map((s) => (
						<div key={s.id} className='flex items-center gap-2 p-3 text-sm'>
							<span className='w-10 font-medium'>{s.name}</span>
							<Badge
								variant={
									s.status === 'paid' && !s.refunded ? 'default' : 'secondary'
								}
							>
								{s.status === 'paid' && s.refunded
									? t('status.slot.refunded')
									: slotLabels[s.status]}
							</Badge>
							<span className='min-w-0 flex-1 truncate'>
								{s.buyerUserId ? (
									canOpenClient ? (
										<Link to={`/admin/clients/${s.buyerUserId}`} className='underline'>
											{buyerLabel(s)}
										</Link>
									) : (
										buyerLabel(s)
									)
								) : (
									(s.buyerName ?? '—')
								)}
							</span>
						</div>
					))}
				</div>
			) : (
				<div className='flex flex-col divide-y rounded-lg border'>
					{lot.inventory.items.map((s) => (
						<div key={s.id} className='flex items-center gap-2 p-3 text-sm'>
							<span className='min-w-0 flex-1 truncate font-medium'>{s.name}</span>
							<span className='tabular-nums text-muted-foreground'>
								{prefixT('stockProgress', {
									sold: String(s.soldQuantity),
									max: String(s.maxQuantity),
								})}
							</span>
						</div>
					))}
				</div>
			)}

			<Button
				variant='outline'
				className='w-full'
				onClick={() => setActionsOpen(true)}
			>
				<MoreHorizontal className='mr-2 size-4' />
				{prefixT('actions')}
			</Button>
			<Drawer open={actionsOpen} onOpenChange={setActionsOpen} showSwipeHandle>
				<DrawerContent>
					<DrawerHeader>
						<DrawerTitle>{prefixT('actions')}</DrawerTitle>
					</DrawerHeader>
					<div className='flex flex-col gap-2 overflow-y-auto p-4'>
						{actionGroups.map((group, index) => (
							<Fragment key={index}>
								{index > 0 && <Separator className='my-1' />}
								{group}
							</Fragment>
						))}
					</div>
				</DrawerContent>
			</Drawer>

			{lot.allocations.length > 0 && (
				<div className='flex flex-col gap-2'>
					<h2 className='text-sm font-semibold'>{prefixT('buyersTitle')}</h2>
					<div className='flex flex-col divide-y rounded-lg border'>
						{lot.allocations.map((allocation) => {
							const trackCodes = trackCodesByBuyer[allocation.buyerUserId] ?? []
							const canEnterCodes = allocationOwedQuantity(allocation) > 0
							const label = buyerLabel(allocation)
							return (
								<div key={allocation.buyerUserId} className='p-3 text-sm'>
									<div className='flex items-center gap-2'>
										<span className='min-w-0 flex-1 truncate font-medium'>
											{canOpenClient ? (
												<Link
													to={`/admin/clients/${allocation.buyerUserId}`}
													className='underline'
												>
													{label}
												</Link>
											) : (
												label
											)}
										</span>
										{canEnterCodes && (
											<IconButton
												size='s'
												mode='bezeled'
												className='!size-7 [&_svg]:size-4'
												aria-label={prefixT('addTrackCodeForBuyer', { buyer: label })}
												title={prefixT('scanTrackCodeHint')}
												onPointerDown={() => startLongPress(allocation.buyerUserId)}
												onPointerUp={clearLongPress}
												onPointerCancel={clearLongPress}
												onPointerLeave={clearLongPress}
												onContextMenu={(event) => event.preventDefault()}
												onClick={(event) => {
													if (longPressHandledRef.current) {
														event.preventDefault()
														longPressHandledRef.current = false
														return
													}
													openScanner(allocation.buyerUserId)
												}}
											>
												<Plus />
											</IconButton>
										)}
									</div>
									<div className='mt-1 text-muted-foreground'>
										{allocation.lines
											.map((line) => `${line.name} × ${line.quantity}`)
											.join(', ')}
									</div>
									{trackCodes.length > 0 && (
										<div className='mt-1.5 flex flex-wrap items-center gap-1'>
											{trackCodes.map((trackCode) => (
												<DropdownMenu
													key={trackCode.id}
													open={openTrackCodeMenuId === trackCode.id}
													onOpenChange={(open) => {
														setOpenTrackCodeMenuId((current) => {
															if (open)
																return current === null || current === trackCode.id
																	? trackCode.id
																	: current
															return current === trackCode.id ? null : current
														})
													}}
												>
													<DropdownMenuTrigger asChild>
														<Button
															type='button'
															variant='outline'
															className='h-6 max-w-44 justify-start rounded-full px-1.5 font-mono text-xs'
															aria-label={prefixT('trackCodeActions', { trackCode: trackCode.value })}
															title={trackCode.value}
															onPointerDown={(event) => {
																if (
																	openTrackCodeMenuId &&
																	openTrackCodeMenuId !== trackCode.id
																) {
																	event.preventDefault()
																	setOpenTrackCodeMenuId(null)
																}
															}}
														>
															<span className='min-w-0 truncate'>
																{trackCode.value}
															</span>
														</Button>
													</DropdownMenuTrigger>
													<DropdownMenuContent
														align='end'
														className='w-auto min-w-32'
													>
														<DropdownMenuItem
															onSelect={() => copyTrackCode(trackCode.value)}
														>
															<Copy />
															{t('common.copy')}
														</DropdownMenuItem>
														{canEnterCodes && (
															<DropdownMenuItem
																onSelect={() =>
																	setCapture({
																		buyerUserId: allocation.buyerUserId,
																		mode: 'manual',
																		editing: trackCode,
																	})
																}
															>
																<Pencil />
																{t('common.edit')}
															</DropdownMenuItem>
														)}
														{canEnterCodes && (
															<DropdownMenuItem
																variant='destructive'
																onSelect={() =>
																	deleteTrackCode(allocation.buyerUserId, trackCode.id)
																}
															>
																<Trash2 />
																{t('common.delete')}
															</DropdownMenuItem>
														)}
													</DropdownMenuContent>
												</DropdownMenu>
											))}
										</div>
									)}
								</div>
							)
						})}
					</div>
				</div>
			)}

			{lot.state === 'purchased' && (
				<>
					<Dialog
						open={flightNameDialogOpen}
						onOpenChange={setFlightNameDialogOpen}
					>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>{prefixT('flightNameTitle')}</DialogTitle>
							</DialogHeader>
							<form
								onSubmit={(event) => {
									event.preventDefault()
									void saveFlightName()
								}}
							>
								<label className='flex flex-col gap-2'>
									<span className='text-sm font-medium'>
										{prefixT('flightNameLabel')}
									</span>
									<Input
										autoFocus
										value={flightNameDraft}
										onChange={(event) => setFlightNameDraft(event.target.value)}
									/>
								</label>
								<DialogFooter className='mt-4'>
									<Button type='submit' disabled={!canSaveFlightName}>
										{prefixT('saveFlightName')}
									</Button>
								</DialogFooter>
							</form>
						</DialogContent>
					</Dialog>
				</>
			)}
			<TrackCodeCaptureModal
				key={
					capture
						? `${capture.buyerUserId}-${capture.editing?.id ?? 'new'}-${capture.mode}`
						: 'closed'
				}
				open={capture !== null}
				mode={capture?.mode ?? 'manual'}
				initialValue={capture?.editing?.value}
				onOpenChange={(open) => {
					if (!open) setCapture(null)
				}}
				onManualEntry={(message) => {
					if (capture) openManualEntry(capture.buyerUserId, message)
				}}
				onSubmit={saveTrackCode}
			/>
		</div>
	)
}
